import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Crown, Check, Sparkles, FileText, Bell, Star, Gift, Users } from "lucide-react";
import { toast } from "sonner";
import { TESTIMONIALS } from "@/data/studentData";

const FEATURES = [
  { icon: FileText, title: "Pack modèles & lettres", detail: "Bail commenté, état des lieux PDF, 8 lettres-types prêtes à signer (résiliation, recours CAF, garant…)" },
  { icon: Sparkles, title: "Sans publicité", detail: "Une expérience épurée, aucune bannière, aucun tracker tiers" },
  { icon: Bell, title: "Accès anticipé aux bons plans", detail: "Bons plans culture/transports/coloc en avant-première (–24 h sur les autres)" },
  { icon: Star, title: "Badge Premium dans la communauté", detail: "Tes annonces remontent en priorité dans le mur étudiant" },
  { icon: Check, title: "Support prioritaire", detail: "Une question ? Réponse en moins de 24 h ouvrées" },
];

export default function Premium() {
  const { user, setUser } = useAuth();
  const [trial, setTrial] = useState(null);
  const [stats, setStats] = useState({ active: 0 });
  const [loadingPay, setLoadingPay] = useState(false);
  const [loadingTrial, setLoadingTrial] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([
          api.get("/premium/trial"),
          api.get("/premium/stats"),
        ]);
        setTrial(t.data);
        setStats(s.data);
      } catch { /* ignore */ }
    })();
  }, [user?.is_premium]);

  const startTrial = async () => {
    setLoadingTrial(true);
    try {
      await api.post("/premium/trial/start");
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Essai 7 jours activé !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation échouée");
    } finally {
      setLoadingTrial(false);
    }
  };

  const subscribe = async () => {
    setLoadingPay(true);
    try {
      const origin = window.location.origin;
      const payload = {
        package_id: "premium-monthly",
        origin_url: origin,
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création paiement échouée");
      setLoadingPay(false);
    }
  };

  const isPremium = user?.is_premium;

  return (
    <div className="space-y-10" data-testid="premium-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Premium</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">
          Passe à Student Hub Premium
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Tout ce que tu aimes déjà, plus des outils pro pour gagner du temps sur les démarches qui comptent. Annulable à tout moment.
        </p>
      </header>

      {/* Social proof */}
      <section className="flex flex-col sm:flex-row gap-4 items-stretch" data-testid="premium-stats">
        <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-yellow-100 text-yellow-700 flex items-center justify-center">
            <Crown size={20} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Étudiants Premium actifs</p>
            <p className="font-display text-2xl font-bold text-slate-900" data-testid="premium-active-count">
              {stats.active}
            </p>
          </div>
        </div>
        <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <Gift size={20} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Essai</p>
            <p className="font-display text-2xl font-bold text-slate-900">7 jours offerts</p>
          </div>
        </div>
        <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center">
            <Users size={20} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Engagement</p>
            <p className="font-display text-2xl font-bold text-slate-900">Zéro</p>
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-5">Inclus dans Premium</h2>
          <ul className="space-y-4">
            {FEATURES.map((f, idx) => (
              <li key={idx} className="flex items-start gap-3" data-testid={`premium-feature-${idx}`}>
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                  <f.icon size={18} />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{f.title}</p>
                  <p className="text-slate-500 text-sm">{f.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border-2 border-yellow-200 bg-yellow-50 p-6 sm:p-8 flex flex-col">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-yellow-800">Premium mensuel</p>
              <p className="font-display text-5xl font-bold text-slate-900 mt-2">
                2,99 € <span className="text-base font-normal text-slate-500">/ mois</span>
              </p>
              <p className="text-slate-600 text-sm mt-2">7 jours offerts. Annulation à tout moment, sans engagement.</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-yellow-300 text-slate-900 flex items-center justify-center shrink-0">
              <Crown size={22} />
            </div>
          </div>
          <div className="mt-auto pt-6">
            {isPremium ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center" data-testid="premium-active">
                <p className="font-display font-semibold text-emerald-700">Tu es déjà Premium ✨</p>
                {user?.premium_until && (
                  <p className="text-xs text-emerald-600 mt-1">
                    Renouvellement / fin : {new Date(user.premium_until).toLocaleDateString("fr-FR")}
                  </p>
                )}
                {trial?.days_left > 0 && trial?.used && (
                  <p className="text-xs text-emerald-600 mt-1">Encore {trial.days_left} jour(s) d'essai</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {trial?.eligible && (
                  <Button
                    onClick={startTrial}
                    disabled={loadingTrial}
                    className="w-full bg-slate-900 hover:bg-slate-800 h-12 text-base font-semibold"
                    data-testid="start-trial-btn"
                  >
                    {loadingTrial ? "Activation..." : "🎁 Démarrer mon essai 7 jours"}
                  </Button>
                )}
                <Button
                  onClick={subscribe}
                  disabled={loadingPay}
                  variant={trial?.eligible ? "outline" : "default"}
                  className={`w-full h-12 text-base font-semibold ${trial?.eligible ? "" : "bg-slate-900 hover:bg-slate-800"}`}
                  data-testid="subscribe-btn"
                >
                  {loadingPay ? "Redirection..." : "S'abonner par carte — 2,99 €/mois"}
                </Button>
                {trial?.used && !isPremium && (
                  <p className="text-xs text-slate-500 text-center">Essai déjà utilisé. Abonne-toi pour continuer.</p>
                )}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-3 text-center">
              Paiement sécurisé via Stripe. Aucune donnée carte ne transite par Student Hub.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="space-y-5" data-testid="testimonials-section">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-semibold text-slate-900">Ils ont déjà sauté le pas</h2>
          <span className="hidden sm:flex items-center gap-1 text-sm text-yellow-600">
            {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-yellow-400 stroke-yellow-400" />)}
            <span className="text-slate-700 ml-1 font-medium">4,9/5</span>
          </span>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col gap-4" data-testid={`testimonial-${t.id}`}>
              <div className="flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={t.name}
                  loading="lazy"
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div>
                  <p className="font-display font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.school} · {t.city}</p>
                </div>
              </div>
              <p className="text-slate-700 text-sm leading-relaxed">"{t.quote}"</p>
              <div className="flex items-center gap-1 text-yellow-500 mt-auto">
                {[...Array(t.rating)].map((_, i) => <Star key={i} size={12} className="fill-yellow-400 stroke-yellow-400" />)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="premium-faq">
        <p className="font-display text-lg font-semibold text-slate-900">Questions fréquentes</p>
        <div className="mt-5 space-y-5 text-sm text-slate-600">
          <div data-testid="faq-cancel">
            <p className="font-semibold text-slate-900 mb-1">Puis-je annuler à tout moment ?</p>
            <p>Oui, sans engagement. Tu peux résilier en 1 clic depuis ta page Premium, ou en répondant à l'email de confirmation. L'accès Premium reste actif jusqu'à la fin de la période déjà payée — aucun prélèvement supplémentaire après.</p>
          </div>
          <div data-testid="faq-free">
            <p className="font-semibold text-slate-900 mb-1">Et si je suis boursier·ère ou en difficulté financière ?</p>
            <p>Premium reste totalement <strong>optionnel</strong>. Toutes les fonctionnalités essentielles — démarches CAF/CROUS/Ameli, documents, parrainage, bien-être, communauté, blog — restent <strong>gratuites pour toujours</strong>, sans publicité ni tracker.</p>
          </div>
          <div data-testid="faq-refund">
            <p className="font-semibold text-slate-900 mb-1">Droit de rétractation ?</p>
            <p>Conformément à la loi française, tu disposes d'un <strong>droit de rétractation de 14 jours</strong> après ton premier paiement, même si tu as déjà utilisé les fonctionnalités Premium. Demande de remboursement par simple email à <a href="mailto:contact@studenthubfrance.fr" className="text-violet-600 hover:underline">contact@studenthubfrance.fr</a>.</p>
          </div>
          <div data-testid="faq-security">
            <p className="font-semibold text-slate-900 mb-1">Mes données sont-elles en sécurité ?</p>
            <p>Oui. Hébergement <strong>en France</strong>, conformité <strong>RGPD</strong>, mots de passe chiffrés (bcrypt), connexion en <strong>HTTPS</strong>, options 2FA et suppression de compte en 1 clic. Aucun tracker tiers, aucune publicité.</p>
          </div>
          <div data-testid="faq-payment">
            <p className="font-semibold text-slate-900 mb-1">Quels moyens de paiement acceptez-vous ?</p>
            <p>Cartes bancaires Visa & Mastercard via <strong>Stripe</strong> (paiement chiffré, certifié PCI-DSS). Aucune information de carte n'est stockée sur nos serveurs.</p>
          </div>
          <div data-testid="faq-ai">
            <p className="font-semibold text-slate-900 mb-1">Comment fonctionne l'assistant IA ?</p>
            <p>L'IA Premium (Claude Sonnet 4.5) t'aide à rédiger ton CV, ta lettre de motivation et simule des entretiens d'embauche, le tout adapté au marché étudiant français. Tes conversations restent privées et ne servent jamais à entraîner un modèle.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
